import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { authClient } from "../../lib/auth-client";
import { trackSignupPageViewed, trackSignupCompleted, identifyUser } from "../../lib/analytics";
import { applyPageSeo } from "../../lib/seo";

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </svg>
  );
}

function Divider() {
  return (
    <div className="relative my-6">
      <div className="absolute inset-0 flex items-center">
        <div className="w-full border-t border-slate-200" />
      </div>
      <div className="relative flex justify-center text-xs">
        <span className="bg-white px-3 text-slate-400">or</span>
      </div>
    </div>
  );
}

export function Signup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get("redirect") || "/dashboard";
  const { data: session, isPending: sessionPending } = authClient.useSession();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // SEO and analytics
  useEffect(() => {
    return applyPageSeo({
      title: "Create Your Free Account",
      description: "Sign up for React Email Builder — free tier includes 3 AI generations/day, 5 exports/month, and 10 saved drafts. No credit card required.",
      path: "/auth/signup",
      robots: "noindex,nofollow",
    });
  }, []);

  useEffect(() => {
    trackSignupPageViewed();
  }, []);

  if (session) {
    return <Navigate to={redirectTo} replace />;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);

    const result = await authClient.signUp.email({
      name,
      email,
      password,
    });

    if (result.error) {
      setError(result.error.message || "Unable to create your account.");
      setIsSubmitting(false);
      return;
    }

    // Identify user in analytics
    const userId = result.data?.user?.id;
    if (userId) {
      identifyUser(userId, { name, email });
    }
    trackSignupCompleted();

    navigate(redirectTo);
  }

  // Password strength calculation
  const passwordStrength = useMemo(() => {
    if (!password) return { score: 0, label: "", color: "" };
    let score = 0;
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[^a-zA-Z0-9]/.test(password)) score++;
    if (score <= 1) return { score, label: "Weak", color: "bg-rose-500" };
    if (score <= 3) return { score, label: "Fair", color: "bg-amber-500" };
    return { score, label: "Strong", color: "bg-emerald-500" };
  }, [password]);

  return (
    <div className="w-full max-w-sm">
      <div className="rounded-md border border-slate-200/80 bg-white/90 p-7 shadow-[0_24px_64px_rgba(15,23,42,0.08)] backdrop-blur">
        <div className="text-center">
          <p className="text-sm font-semibold uppercase tracking-[0.22em] text-sky-600">
            Free to get started
          </p>
          <h1 className="mt-3 text-2xl font-semibold tracking-tight text-slate-950">
            Create your account
          </h1>
          <p className="mt-2 text-sm text-slate-500">
            Start building emails with AI. No credit card required.
          </p>
        </div>

        <div className="mt-6 grid grid-cols-3 gap-3">
          <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5 text-center">
            <p className="text-lg font-semibold text-slate-950">148+</p>
            <p className="text-[10px] font-medium uppercase tracking-[0.16em] text-slate-500">Templates</p>
          </div>
          <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5 text-center">
            <p className="text-lg font-semibold text-slate-950">3</p>
            <p className="text-[10px] font-medium uppercase tracking-[0.16em] text-slate-500">AI gen/day</p>
          </div>
          <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5 text-center">
            <p className="text-lg font-semibold text-slate-950">5</p>
            <p className="text-[10px] font-medium uppercase tracking-[0.16em] text-slate-500">Exports/mo</p>
          </div>
        </div>

        <form className="mt-6 space-y-5" onSubmit={(event) => void handleSubmit(event)}>
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-slate-700">
              Name
            </label>
            <input
              id="name"
              type="text"
              autoComplete="name"
              placeholder="Jane Doe"
              value={name}
              onChange={(event) => setName(event.target.value)}
              disabled={isSubmitting || sessionPending}
              required
              className="mt-1.5 block w-full rounded-md border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-sky-500 focus:outline-none focus:ring-4 focus:ring-sky-500/10"
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-slate-700">
              Email
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              placeholder="you@example.com"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              disabled={isSubmitting || sessionPending}
              required
              className="mt-1.5 block w-full rounded-md border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-sky-500 focus:outline-none focus:ring-4 focus:ring-sky-500/10"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-slate-700">
              Password
            </label>
            <input
              id="password"
              type="password"
              autoComplete="new-password"
              placeholder="••••••••"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              disabled={isSubmitting || sessionPending}
              required
              className="mt-1.5 block w-full rounded-md border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-sky-500 focus:outline-none focus:ring-4 focus:ring-sky-500/10"
            />
            {password.length > 0 && (
              <div className="mt-2">
                <div className="flex items-center justify-between">
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <div
                        key={i}
                        className={`h-1.5 w-full rounded-full transition-colors ${
                          i <= passwordStrength.score
                            ? passwordStrength.color
                            : "bg-slate-200"
                        }`
                      }
                      />
                    ))}
                  </div>
                  <span className={`text-xs font-medium ${
                    passwordStrength.label === "Weak" ? "text-rose-600" :
                    passwordStrength.label === "Fair" ? "text-amber-600" :
                    "text-emerald-600"
                  }`}>
                    {passwordStrength.label}
                  </span>
                </div>
                <p className="mt-1 text-[11px] text-slate-400">Use 8+ characters with a mix of letters, numbers, and symbols</p>
              </div>
            )}
          </div>

          {error ? (
            <p className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700" role="alert">
              {error}
            </p>
          ) : null}

          <button
            type="submit"
            disabled={isSubmitting || sessionPending}
            className="w-full rounded bg-slate-950 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-950 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSubmitting ? "Creating account..." : "Create free account"}
          </button>
        </form>

        <p className="mt-4 text-center text-xs text-slate-400">
          By signing up, you agree to our{" "}
          <Link to="/legal/terms" className="font-medium text-slate-500 underline transition hover:text-slate-700">Terms of Service</Link>{" "}
          and{" "}
          <Link to="/legal/privacy" className="font-medium text-slate-500 underline transition hover:text-slate-700">Privacy Policy</Link>.
        </p>
      </div>

      <p className="mt-6 text-center text-sm text-slate-500">
        Already have an account?{" "}
        <Link to={"/auth/login" + (searchParams.get("redirect") ? "?redirect=" + searchParams.get("redirect") : "")} className="font-semibold text-sky-600 transition hover:text-sky-500">
          Sign in
        </Link>
      </p>
    </div>
  );
}